import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/apiClient";
import ProductCard from "@/components/ProductCard";
import { useLang } from "@/contexts/LanguageContext";
import useSeo from "@/hooks/useSeo";

const FALLBACK_LABELS = {
  "new-bikes": "New Bikes",
  "used-bikes": "Used Bikes",
  "e-bikes": "E-Bikes",
  parts: "Parts",
  accessories: "Accessories",
};

export default function ShopPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const category = params.category;
  const searchQuery = searchParams.get("search") || "";

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [facets, setFacets] = useState({ brands: [], wheel_sizes: [], frame_sizes: [], motor_brands: [], subcategories: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ brand: "", wheel_size: "", frame_size: "", motor_brand: "", subcategory: "", sort: "default", min_price: "", max_price: "" });

  // Reset subcategory when category route changes
  useEffect(() => {
    setFilters((f) => ({ ...f, subcategory: "" }));
  }, [category]);

  const query = useMemo(() => {
    const q = { limit: 60 };
    if (category) q.category = category;
    if (searchQuery) q.search = searchQuery;
    if (filters.brand) q.brand = filters.brand;
    if (filters.wheel_size) q.wheel_size = filters.wheel_size;
    if (filters.frame_size) q.frame_size = filters.frame_size;
    if (filters.motor_brand) q.motor_brand = filters.motor_brand;
    if (filters.subcategory) q.subcategory = filters.subcategory;
    if (filters.min_price !== "") q.min_price = filters.min_price;
    if (filters.max_price !== "") q.max_price = filters.max_price;
    return q;
  }, [category, searchQuery, filters]);

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: query })
      .then((r) => {
        let list = [...r.data];
        if (filters.sort === "price-asc") list.sort((a, b) => a.price - b.price);
        if (filters.sort === "price-desc") list.sort((a, b) => b.price - a.price);
        setProducts(list);
      })
      .finally(() => setLoading(false));
  }, [query, filters.sort]);

  useEffect(() => {
    api.get("/products/facets", { params: category ? { category } : {} }).then((r) => setFacets(r.data));
  }, [category]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
  }, []);

  const currentCategory = categories.find((c) => c.key === category);
  const managedSubs = currentCategory?.subcategories || [];
  // Prefer admin-managed subcategories; fall back to whatever products actually use
  const availableSubs = managedSubs.length ? managedSubs : facets.subcategories;
  const label = category
    ? (currentCategory?.label || FALLBACK_LABELS[category] || category)
    : (searchQuery ? `Search: ${searchQuery}` : "All Products");

  useSeo({
    title: label,
    description: `Shop ${label.toLowerCase()} at VELOTEKA — premium quality, imported from Germany, fast Balkans delivery.`,
    path: category ? `/shop/${category}` : "/shop",
  });

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12" data-testid="shop-page">
      <div className="mb-10">
        <div className="overline text-[color:var(--vt-orange)] mb-3">/ SHOP</div>
        <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter">{label}</h1>
        {category && availableSubs.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2" data-testid="subcategory-pills">
            <button
              onClick={() => setFilters({ ...filters, subcategory: "" })}
              className={`px-4 py-1.5 text-xs uppercase font-bold tracking-widest border transition-colors ${
                filters.subcategory === ""
                  ? "bg-black text-white border-black"
                  : "bg-white text-neutral-700 border-neutral-300 hover:border-black"
              }`}
              data-testid="subcategory-pill-all"
            >
              All
            </button>
            {availableSubs.map((s) => (
              <button
                key={s}
                onClick={() => setFilters({ ...filters, subcategory: s })}
                className={`px-4 py-1.5 text-xs uppercase font-bold tracking-widest border transition-colors ${
                  filters.subcategory === s
                    ? "bg-black text-white border-black"
                    : "bg-white text-neutral-700 border-neutral-300 hover:border-black"
                }`}
                data-testid={`subcategory-pill-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        <aside className="border border-neutral-200 p-6 h-fit lg:sticky lg:top-32" data-testid="shop-filters">
          <h3 className="font-display font-bold text-lg mb-4">Filters</h3>
          <div className="space-y-6 text-sm">
            {availableSubs.length > 0 && (
              <div>
                <label className="overline text-neutral-600 block mb-2">Type</label>
                <select className="vt-input" value={filters.subcategory} onChange={(e) => setFilters({ ...filters, subcategory: e.target.value })} data-testid="filter-subcategory">
                  <option value="">All</option>
                  {availableSubs.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            {facets.brands.length > 0 && (
              <div>
                <label className="overline text-neutral-600 block mb-2">Brand</label>
                <select className="vt-input" value={filters.brand} onChange={(e) => setFilters({ ...filters, brand: e.target.value })} data-testid="filter-brand">
                  <option value="">All</option>
                  {facets.brands.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            )}
            {facets.wheel_sizes.length > 0 && (
              <div>
                <label className="overline text-neutral-600 block mb-2">Wheel Size</label>
                <select className="vt-input" value={filters.wheel_size} onChange={(e) => setFilters({ ...filters, wheel_size: e.target.value })}>
                  <option value="">All</option>
                  {facets.wheel_sizes.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
            )}
            {facets.frame_sizes.length > 0 && (
              <div>
                <label className="overline text-neutral-600 block mb-2">Frame Size</label>
                <select className="vt-input" value={filters.frame_size} onChange={(e) => setFilters({ ...filters, frame_size: e.target.value })}>
                  <option value="">All</option>
                  {facets.frame_sizes.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            )}
            {facets.motor_brands.length > 0 && (
              <div>
                <label className="overline text-neutral-600 block mb-2">Motor Brand</label>
                <select className="vt-input" value={filters.motor_brand} onChange={(e) => setFilters({ ...filters, motor_brand: e.target.value })}>
                  <option value="">All</option>
                  {facets.motor_brands.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="overline text-neutral-600 block mb-2">Price (€)</label>
              <div className="flex gap-2">
                <input
                  type="number" min="0" placeholder="Min"
                  className="vt-input"
                  value={filters.min_price}
                  onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                  data-testid="filter-min-price"
                />
                <input
                  type="number" min="0" placeholder="Max"
                  className="vt-input"
                  value={filters.max_price}
                  onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                  data-testid="filter-max-price"
                />
              </div>
            </div>
            <div>
              <label className="overline text-neutral-600 block mb-2">Sort By</label>
              <select className="vt-input" value={filters.sort} onChange={(e) => setFilters({ ...filters, sort: e.target.value })} data-testid="filter-sort">
                <option value="default">Default</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
              </select>
            </div>
            <button
              className="vt-btn-outline w-full"
              onClick={() => setFilters({ brand: "", wheel_size: "", frame_size: "", motor_brand: "", subcategory: "", sort: "default", min_price: "", max_price: "" })}
              data-testid="filter-reset"
            >
              Reset
            </button>
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="text-center py-24 text-neutral-500">Loading...</div>
          ) : products.length === 0 ? (
            <div className="text-center py-24 border border-dashed border-neutral-300" data-testid="no-products">
              <p className="text-neutral-500">No products found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="products-grid">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
